import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { DOWNLOAD_LINK } from '../files/blob-file';
import { SessionRefreshService } from './session-refresh.service';

function ownApiPath(url: string): string | null {
  try {
    const parsed = new URL(url, `${window.location.origin}/`);
    if (parsed.origin !== window.location.origin) {
      return null;
    }
    const path = parsed.pathname;
    return path.startsWith('/api/') || path.startsWith('/health/')
      ? path
      : null;
  } catch {
    return null;
  }
}

/** Anmelde-Endpunkte selbst lösen keinen Refresh aus (sonst Endlosschleife). */
function isAuthEndpoint(path: string): boolean {
  return path.startsWith('/api/v1/auth/');
}

function isUnauthorized(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 401;
}

/**
 * Sendet die Session-Cookies an das eigene Backend (nicht an fremde Server) und
 * erneuert bei einem 401 einmalig das Access-Token, bevor der Request wiederholt wird.
 */
export const httpAuthInterceptor: HttpInterceptorFn = (request, next) => {
  const path = ownApiPath(request.url);
  if (!path) {
    return next(request);
  }

  const authenticated: HttpRequest<unknown> = request.clone({
    withCredentials: true,
    setHeaders: {
      // Uploads am Service Worker vorbei: Safari (iOS) verliert sonst den
      // Dateiinhalt, wenn der Worker den Request per fetch() weiterreicht.
      ...(request.body instanceof FormData ? { 'ngsw-bypass': 'true' } : {}),
      // Datei für den Tab als Link statt als Blob (siehe openBlob).
      ...(request.context.get(DOWNLOAD_LINK) ? { 'X-Download-Link': '1' } : {}),
    },
  });
  if (isAuthEndpoint(path)) {
    return next(authenticated);
  }

  const refresher = inject(SessionRefreshService);
  return next(authenticated).pipe(
    catchError((error: unknown) => {
      if (!isUnauthorized(error)) {
        return throwError(() => error);
      }
      return refresher.refresh().pipe(
        // Refresh fehlgeschlagen: ursprüngliches 401 weitergeben (Session abgelaufen).
        catchError(() => throwError(() => error)),
        switchMap(() => next(authenticated)),
      );
    }),
  );
};
